import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getSite, listSites, getUniqueAlarmPaths, getVirtualTenantAlarmPaths } from '../../services/siteManagement/siteManagement.service';
import { getCustomerSites } from '../../services/customer/customer.service';
import { listCategories } from '../../services/category/category.service';
import type { CategorySummary } from '../../services/category/category.types';
import { getAlarmsBySite, localAcknowledge } from '../../services/bureauManagement/bureauManagement.service';
import type { RuleSummary, CreateRuleRequest, RuleDetail as ApiRuleDetail } from '../../services/ruleManagement/ruleManagement.types';
import type { VirtualTenantDetail } from '../../services/siteManagement/siteManagement.types';
import { formatDateTime } from '../../helpers/formatDateTime';
import { getAlarmSections, alarmSectionsToText, alarmSectionsToHtml } from '../../helpers/formatAlarmClipboard';
import { DateTimeDisplay, ActionList, StatusMessage, Breadcrumbs, SearchQuery } from "../../components/ui";
import { TOAST_LONG_MS, TOAST_SUCCESS_MS } from '../../constants/timeouts';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Modal from '../../components/ui/Modal';
import { ModalContent } from '../../components/ui/ModalContent';
import RulesForm, { type RuleFormData } from '../../components/forms/RulesForm';
import { createRule, patchRuleStatus, getRule, updateRule, deleteRule, listSiteRules, listAllRules } from '../../services/ruleManagement/ruleManagement.service';
import VirtualTenantsForm from '../../components/forms/VirtualTenantsForm';
import VirtualTenantsList from '../../components/tables/VirtualTenantsList';
import { BureauCategoryTable } from '../../components/alarms/category/views/BureauCategoryTable';
import Pagination from '../../components/ui/Pagination';
import type { BureauAlarm } from '../../services/bureauManagement/bureauManagement.types';
import BulkActionToolbar from '../../components/ui/BullActionToolbar';
import BureauFilter from '../../components/filters/BureauFilter';
import { FilterBtn } from '../../components/ui/Buttons';
import { getBureauCategoryLabel } from '../../components/alarms/category/constants';
import BureauAcknowledgeForm from '../../components/forms/BureauAcknowledgeForm';
import { getProfile } from '../../services/userManagement/userManagement.service';
import { useAuthContext } from '../../contexts/useAuthContext';
import { alarmAcknowledgedEvent } from '../../services/events/alarmEvents';

type VirtualTenantLike = {
  id?: number;
  name?: string;
  siteIdentifier?: string;
  virtualTenantIdentifier?: string;
  parentSiteHierarchyId?: number;
  entityType?: string;
  isVirtualTenant?: boolean;
  ruleCount?: number;
  [key: string]: unknown;
};

type SiteDetail = {
  id?: number;
  name?: string;
  siteName?: string;
  displayName?: string;
  siteIdentifier?: string;
  customerName?: string;
  customerId?: number;
  children?: unknown[];
  siteGuid?: string;
  siteGUID?: string;
  guid?: string;
  systemTypeName?: string;
  region?: string;
  statusDescription?: string;
  registryStatus?: string;
  status?: string;
  updatedAt?: string;
  modifiedAt?: string;
  createdAt?: string;
  siteCategoryId?: number | null;
  siteCategoryName?: string | null;
  [key: string]: unknown;
};

type RuleDetail = ApiRuleDetail;

type ClipboardNavigator = Navigator & {
  clipboard?: Clipboard & { write?: (items: ClipboardItem[]) => Promise<void> };
};

type ClipboardGlobal = typeof globalThis & {
  ClipboardItem?: new (data: Record<string, Blob>) => ClipboardItem;
};

type VirtualTenantRulesContainer = {
  id?: number;
  virtualTenantId?: number;
  siteHierarchyId?: number;
  siteIdentifierRegistryId?: number;
  name?: string;
  rules?: RuleSummary[];
};

const normalizeNameKey = (value: unknown): string => {
  return String(value ?? '').trim().toLowerCase();
};

const buildVirtualTenantRuleCountMap = (virtualTenants: unknown): Record<string, number> => {
  const counts: Record<string, number> = {};
  if (!Array.isArray(virtualTenants)) {
    return counts;
  }

  virtualTenants.forEach((item: unknown) => {
    if (typeof item !== 'object' || item === null) {
      return;
    }

    const vt = item as VirtualTenantRulesContainer;
    const ruleCount = Array.isArray(vt.rules) ? vt.rules.length : 0;
    if (ruleCount <= 0) {
      return;
    }

    const idCandidates = [vt.id, vt.virtualTenantId, vt.siteHierarchyId, vt.siteIdentifierRegistryId]
      .map(id => Number(id))
      .filter(id => !Number.isNaN(id) && id > 0);

    idCandidates.forEach(id => {
      const key = String(id);
      counts[key] = Math.max(counts[key] || 0, ruleCount);
    });

    const nameKey = normalizeNameKey(vt.name);
    if (nameKey) {
      const key = `name:${nameKey}`;
      counts[key] = Math.max(counts[key] || 0, ruleCount);
    }
  });

  return counts;
};

const extractRuleIdFromUnknown = (value: unknown): number | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const candidate = obj.siteRuleId ?? obj.ruleId ?? obj.id;
  const numericId = Number(candidate);
  if (Number.isNaN(numericId) || numericId <= 0) {
    return null;
  }

  return numericId;
};

const collectVirtualTenantRuleIdsFromContainers = (virtualTenants: unknown): Set<number> => {
  const vtRuleIds = new Set<number>();
  if (!Array.isArray(virtualTenants)) {
    return vtRuleIds;
  }

  virtualTenants.forEach((item: unknown) => {
    if (typeof item !== 'object' || item === null) {
      return;
    }
    const vt = item as VirtualTenantRulesContainer;
    if (!Array.isArray(vt.rules)) {
      return;
    }
    vt.rules.forEach(rule => {
      const ruleId = extractRuleIdFromUnknown(rule);
      if (ruleId !== null) {
        vtRuleIds.add(ruleId);
      }
    });
  });

  return vtRuleIds;
};

const resolveVirtualTenantRuleCount = (vt: VirtualTenantLike, vtRuleCountMap: Record<string, number>): number => {
  const idCandidates = [vt.id, vt.parentSiteHierarchyId]
    .map(id => Number(id))
    .filter(id => !Number.isNaN(id) && id > 0);

  for (const id of idCandidates) {
    const byId = vtRuleCountMap[String(id)];
    if (typeof byId === 'number' && byId > 0) {
      return byId;
    }
  }

  const nameCandidates = [vt.name, vt.virtualTenantIdentifier, vt.siteIdentifier]
    .map(normalizeNameKey)
    .filter(Boolean);

  for (const name of nameCandidates) {
    const byName = vtRuleCountMap[`name:${name}`];
    if (typeof byName === 'number' && byName > 0) {
      return byName;
    }
  }

  return Number(vt.ruleCount) || 0;
};

const toVirtualTenants = (children: unknown[] | undefined): VirtualTenantLike[] => {
  return (children || []).filter((child): child is VirtualTenantLike => {
    const item = child as VirtualTenantLike;
    return item.entityType === 'VirtualTenant' || item.isVirtualTenant === true;
  });
};

const extractRuleAlarmPathIds = (alarmPaths: unknown): number[] => {
  if (!Array.isArray(alarmPaths)) {
    return [];
  }

  const ids: number[] = [];
  alarmPaths.forEach((item: unknown) => {
    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>;
      const maybeId = obj.id ?? obj.alarmPathId;
      const numericId = Number(maybeId);
      if (!Number.isNaN(numericId) && numericId > 0) {
        ids.push(numericId);
      }
    }
  });

  return ids;
};

const extractRuleAlarmPathLabelsById = (alarmPaths: unknown): Record<string, string> => {
  if (!Array.isArray(alarmPaths)) {
    return {};
  }

  const labelsById: Record<string, string> = {};
  alarmPaths.forEach((item: unknown) => {
    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>;
      const maybeId = obj.id ?? obj.alarmPathId;
      const numericId = Number(maybeId);
      const pathToAlarm = typeof obj.pathToAlarm === 'string' ? obj.pathToAlarm : '';
      if (!Number.isNaN(numericId) && numericId > 0 && pathToAlarm) {
        labelsById[String(numericId)] = pathToAlarm;
      }
    }
  });

  return labelsById;
};

const extractVirtualTenantAlarmPathIds = (data: unknown): number[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  const ids: number[] = [];
  data.forEach((item: unknown) => {
    if (typeof item === 'number' && item > 0) {
      ids.push(item);
      return;
    }

    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>;
      const maybeId = obj.alarmPathId ?? obj.id;
      const numericId = Number(maybeId);
      if (!Number.isNaN(numericId) && numericId > 0) {
        ids.push(numericId);
      }
    }
  });

  return ids;
};

type VirtualTenantPathMatchResult = {
  vtRuleIds: Set<number>;
  vtRuleCountMap: Record<string, number>;
  matchedVirtualTenantIdsByRuleId: Record<number, number>;
};

const resolveVirtualTenantRuleIdsByPathMatch = async (
  rules: RuleSummary[],
  virtualTenants: VirtualTenantLike[] | null | undefined
): Promise<VirtualTenantPathMatchResult> => {
  const matchedRuleIds = new Set<number>();
  const vtRuleCountMap: Record<string, number> = {};
  const matchedVirtualTenantIdsByRuleId: Record<number, number> = {};
  if (!Array.isArray(rules) || rules.length === 0 || !Array.isArray(virtualTenants) || virtualTenants.length === 0) {
    return { vtRuleIds: matchedRuleIds, vtRuleCountMap, matchedVirtualTenantIdsByRuleId };
  }

  const vtPathCache = new Map<number, Set<number>>();
  const rulePathCache = new Map<number, Set<number>>();

  await Promise.all(
    rules.map(async rule => {
      const ruleId = Number(rule.siteRuleId);
      if (Number.isNaN(ruleId) || ruleId <= 0) {
        return;
      }

      try {
        const detailRes = await getRule(ruleId);
        const detailPathSet = new Set(extractRuleAlarmPathIds(detailRes?.data?.alarmPaths));
        rulePathCache.set(ruleId, detailPathSet);
      } catch {
        rulePathCache.set(ruleId, new Set<number>());
      }
    })
  );

  await Promise.all(
    virtualTenants.map(async vt => {
      const vtId = Number(vt.id);
      if (Number.isNaN(vtId) || vtId <= 0) {
        return;
      }
      try {
        const vtPathRes = await getVirtualTenantAlarmPaths(vtId);
        const vtPathSet = new Set(extractVirtualTenantAlarmPathIds(vtPathRes?.data));
        vtPathCache.set(vtId, vtPathSet);
      } catch {
        vtPathCache.set(vtId, new Set<number>());
      }
    })
  );

  rules.forEach(rule => {
    const ruleId = Number(rule.siteRuleId);
    if (Number.isNaN(ruleId) || ruleId <= 0) {
      return;
    }

    const rulePathSet = rulePathCache.get(ruleId) || new Set<number>();
    if (rulePathSet.size === 0) {
      return;
    }

    const subsetMatches: number[] = [];
    vtPathCache.forEach((vtPathSet, vtId) => {
      if (vtPathSet.size === 0 || rulePathSet.size > vtPathSet.size) {
        return;
      }
      let allPresent = true;
      for (const pathId of rulePathSet) {
        if (!vtPathSet.has(pathId)) {
          allPresent = false;
          break;
        }
      }
      if (allPresent) {
        subsetMatches.push(vtId);
      }
    });

    if (subsetMatches.length === 1) {
      const matchedVtId = subsetMatches[0];
      matchedRuleIds.add(ruleId);
      matchedVirtualTenantIdsByRuleId[ruleId] = matchedVtId;
      const key = String(matchedVtId);
      vtRuleCountMap[key] = (vtRuleCountMap[key] || 0) + 1;
    }
  });

  return { vtRuleIds: matchedRuleIds, vtRuleCountMap, matchedVirtualTenantIdsByRuleId };
};

const SiteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pathsCount, setPathsCount] = useState<number | null>(null);
  const [virtualTenants, setVirtualTenants] = useState<VirtualTenantLike[] | null>(null);
  const [siteRules, setSiteRules] = useState<RuleSummary[] | null>(null);
  const [siteRuleDetails, setSiteRuleDetails] = useState<Record<number, RuleDetail>>({});
  const [siteHierarchyId, setSiteHierarchyId] = useState<number | null>(null);
  const [siteIdentifierRegistryId, setSiteIdentifierRegistryId] = useState<number | null>(null);
  const [rulesLoading, setRulesLoading] = useState<boolean>(false);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [editingRuleData, setEditingRuleData] = useState<RuleFormData | null>(null);
  const [newRules, setNewRules] = useState<RuleFormData[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isCreatingRules, setIsCreatingRules] = useState<boolean>(false);
  const [bureauAlarms, setBureauAlarms] = useState<BureauAlarm[]>([]);
  const [alarmsLoading, setAlarmsLoading] = useState<boolean>(false);
  const [alarmsTotal, setAlarmsTotal] = useState<number>(0);
  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});
  const [alarmsRefreshCount, setAlarmsRefreshCount] = useState<number>(0);
  const [bulkMode, setBulkMode] = useState<'acknowledge' | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [ackSuccessMessage, setAckSuccessMessage] = useState<string | null>(null);
  const [ackErrorMessage, setAckErrorMessage] = useState<string | null>(null);
  const [isCreateFormValid, setIsCreateFormValid] = useState<boolean>(false);
  const [isEditFormValid, setIsEditFormValid] = useState<boolean>(false);
  const [showCreateErrorSummary, setShowCreateErrorSummary] = useState<boolean>(false);
  const [showEditErrorSummary, setShowEditErrorSummary] = useState<boolean>(false);
  const [isVTModalOpen, setIsVTModalOpen] = useState<boolean>(false);
  const [isVTEditModalOpen, setIsVTEditModalOpen] = useState<boolean>(false);
  const [editingVT, setEditingVT] = useState<VirtualTenantLike | null>(null);
  const [availableCategories, setAvailableCategories] = useState<CategorySummary[]>([]);

  // Confirmation modal state for rule actions
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('Confirm');
  const confirmResolverRef = useRef<((confirmed: boolean) => void) | undefined>(undefined);
  const confirmModalHeaderClass = /(delete|deactivate)/i.test(confirmModalTitle)
    ? 'modal__header--danger'
    : undefined;

  const enrichRuleWithCurrentSiteContext = <T extends {
    siteIdentifierRegistryId?: number;
    siteName?: string;
    siteIdentifier?: string;
  }>(rule: T): T => {
    const currentSiteRegistryId = Number(siteIdentifierRegistryId ?? id);
    const ruleRegistryId = Number(rule.siteIdentifierRegistryId);

    if (Number.isNaN(currentSiteRegistryId) || currentSiteRegistryId <= 0) {
      return rule;
    }

    if (Number.isNaN(ruleRegistryId) || ruleRegistryId !== currentSiteRegistryId) {
      return rule;
    }

    return {
      ...rule,
      siteName: site?.siteName ?? site?.name ?? site?.displayName ?? rule.siteName,
      siteIdentifier: site?.siteIdentifier ?? rule.siteIdentifier,
    };
  };

  // Helper to show confirmation modal and return a promise
  const showConfirmModal = (message: string, title = 'Confirm'): Promise<boolean> => {
    setConfirmModalMessage(message);
    setConfirmModalTitle(title);
    setConfirmModalOpen(true);
    return new Promise(resolve => {
      confirmResolverRef.current = resolve;
    });
  };
  const { userProfile } = useAuthContext();
  const siteRulesRef = useRef<RuleSummary[] | null>(null);

  // Search and filter state for bureau alarms
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('active-not-ack');
  const [bureauPage, setBureauPage] = useState<number>(1);
  const [bureauPageSize] = useState<number>(100);
  const [bureauTotalPages, setBureauTotalPages] = useState<number>(1);
  const [bureauSortColumn, setBureauSortColumn] = useState<string>('lastOccurrenceAt');
  const [bureauSortDirection, setBureauSortDirection] = useState<'asc' | 'desc'>('desc');
  const [siteCategoryCounts, setSiteCategoryCounts] = useState<Record<string, number>>({
    all: 0,
    'active-not-ack': 0,
    'active-ack': 0,
    'not-active-not-ack': 0,
  });

  // URL search params sync (q = search query, cat = category)
  const [searchParams, setSearchParams] = useSearchParams();

  const storageKey = `site:${id ?? 'global'}:filters`;

  // Initialize filter/search state from URL query params or localStorage
  useEffect(() => {
    try {
      const fromUrlQ = searchParams.get('q');
      const fromUrlCat = searchParams.get('cat');

      const raw = localStorage.getItem(storageKey);
      const stored = raw ? JSON.parse(raw) : {};

      const q = fromUrlQ ?? stored.q ?? '';
      const cat = fromUrlCat ?? stored.cat ?? 'active-not-ack';

      setSearchQuery(q);
      setSelectedCategory(cat);
    } catch (e) {
      // fallback to defaults
      setSearchQuery('');
      setSelectedCategory('active-not-ack');
    }
    // run when id or URL params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchParams.toString()]);

  // Persist category to URL and localStorage (remove param when 'all')
  useEffect(() => {
    try {
      const params = new URLSearchParams(searchParams);
      if (selectedCategory && selectedCategory !== 'all') {
        params.set('cat', selectedCategory);
      } else {
        params.delete('cat');
      }
      setSearchParams(params, { replace: true });

      const raw = localStorage.getItem(storageKey);
      const stored = raw ? JSON.parse(raw) : {};
      stored.cat = selectedCategory;
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch (e) {
      console.info('[SiteDetails] Failed to persist category to storage', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Debounced sync for searchQuery -> URL `q` and localStorage `q`
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const params = new URLSearchParams(searchParams);
        if (searchQuery && searchQuery.trim()) {
          params.set('q', searchQuery.trim());
        } else {
          params.delete('q');
        }
        setSearchParams(params, { replace: true });

        const raw = localStorage.getItem(storageKey);
        const stored = raw ? JSON.parse(raw) : {};
        stored.q = searchQuery && searchQuery.trim() ? searchQuery.trim() : '';
        localStorage.setItem(storageKey, JSON.stringify(stored));
      } catch (e) {
        console.info('[SiteDetails] Clipboard fallback failed', e);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Keep ref in sync with state for use in callbacks
  useEffect(() => {
    siteRulesRef.current = siteRules;
  }, [siteRules]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await listCategories();
        if (res?.success && res.data) {
          setAvailableCategories(res.data);
        }
      } catch (err) {
        console.info('Failed to load categories', err);
      }
    };

    void fetchCategories();
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getSite(id);
        if (!mounted) {
          return;
        }
        if (res?.success && res.data) {
          
          // Merge fields that only exist on list endpoints.
          const detailSiteIdentifier = res.data?.siteIdentifier;
          const detailSiteRegistryId = Number(
            res.data?.siteIdentifierRegistryId ??
            res.data?.siteRegistryId ??
            id
          );
          const routeSiteRegistryId = Number(id);
          const detailCustomerId = Number(res.data?.customerId);

          const applyListMatchToSite = (
            items: Array<{
              id: number;
              siteIdentifier?: string;
              siteGuid?: string;
              systemTypeName?: string;
              region?: string;
              statusDescription?: string;
            }>
          ) => {
            const normalizedDetailIdentifier = typeof detailSiteIdentifier === 'string'
              ? detailSiteIdentifier.trim().toLowerCase()
              : '';

            const match = items.find(s => {
              const normalizedListIdentifier = typeof s.siteIdentifier === 'string'
                ? s.siteIdentifier.trim().toLowerCase()
                : '';

              if (normalizedDetailIdentifier && normalizedListIdentifier === normalizedDetailIdentifier) {
                return true;
              }

              if (!Number.isNaN(routeSiteRegistryId) && Number(s.id) === routeSiteRegistryId) {
                return true;
              }

              if (!Number.isNaN(detailSiteRegistryId) && Number(s.id) === detailSiteRegistryId) {
                return true;
              }

              return false;
            });

            if (match) {
              const matchRecord = match as Record<string, unknown>;
              const guidFromMatch =
                (typeof match.siteGuid === 'string' && match.siteGuid.trim() ? match.siteGuid.trim() : undefined)
                || (typeof matchRecord.siteGUID === 'string' && matchRecord.siteGUID.trim() ? matchRecord.siteGUID.trim() : undefined)
                || (typeof matchRecord.guid === 'string' && matchRecord.guid.trim() ? matchRecord.guid.trim() : undefined)
                || (typeof matchRecord.SiteGuid === 'string' && matchRecord.SiteGuid.trim() ? matchRecord.SiteGuid.trim() : undefined);

              setSite((prev) => ({
                ...prev,
                siteGuid: guidFromMatch ?? prev?.siteGuid ?? prev?.siteGUID ?? prev?.guid,
                systemTypeName: match.systemTypeName ?? prev?.systemTypeName,
                region: match.region ?? prev?.region,
                statusDescription: match.statusDescription ?? prev?.statusDescription,
              }));
            }
          };

          if (!Number.isNaN(detailCustomerId) && detailCustomerId > 0) {
            getCustomerSites(detailCustomerId)
              .then(sitesRes => {
                if (sitesRes?.success && Array.isArray(sitesRes.data) && mounted) {
                  applyListMatchToSite(sitesRes.data);
                  return;
                }

                return listSites().then(listRes => {
                  if (listRes?.success && Array.isArray(listRes.data) && mounted) {
                    applyListMatchToSite(listRes.data);
                  }
                });
              })
              .catch(() => {
                listSites().then(listRes => {
                  if (listRes?.success && Array.isArray(listRes.data) && mounted) {
                    applyListMatchToSite(listRes.data);
                  }
                }).catch(() => {});
              });
          } else {
            listSites().then(listRes => {
              if (listRes?.success && Array.isArray(listRes.data) && mounted) {
                applyListMatchToSite(listRes.data);
              }
            }).catch(() => {});
          }

          setSite(res.data as unknown as SiteDetail);
          
          // CRITICAL: Use the URL parameter ID as the canonical registry ID
          // The user navigated to /sites/{id}, so that IS the registry ID we should use
          // Don't trust backend responses that might return cross-mapped IDs
          const registryId = id; // Use URL parameter directly
          if (registryId) {
            setSiteIdentifierRegistryId(Number(registryId));
            }
          
          // Extract VTs from children array (filter for VirtualTenant entityType)
          const vts = toVirtualTenants(res.data.children);
          setVirtualTenants(vts);
          
            // fetch alarm paths in parallel
          const pathsRes = await getUniqueAlarmPaths(id);
          
          if (!mounted) {
            return;
          }
          if (pathsRes?.success) {
            setPathsCount(pathsRes.data?.length ?? 0);
          }

          // Fetch bureau alarms for this site - use same approach as homepage/sidebar
          // Also fetch rules associated with this site
          setRulesLoading(true);
          let initialSiteRules: RuleSummary[] = [];
          let initialVtRuleCountMap: Record<string, number> = {};
          try {
            // CRITICAL: Use the URL parameter ID (which we just set as siteIdentifierRegistryId)
            // This ensures we're fetching rules for the site the user navigated to
            const siteRegistryId = id; // Use URL parameter directly
            
            try {
              const rulesRes = await listSiteRules(siteRegistryId);
              
              if (rulesRes?.success && rulesRes.data) {
                
                // Capture IDs from the response for rule creation
                if (rulesRes.data.siteHierarchyId) {
                  setSiteHierarchyId(rulesRes.data.siteHierarchyId);
                }
                // Don't let backend overwrite with wrong ID!
                if (rulesRes.data.siteIdentifierRegistryId) {
                  if (Number(rulesRes.data.siteIdentifierRegistryId) !== Number(siteRegistryId)) {
                    console.info('[SiteDetails] Backend returned DIFFERENT siteIdentifierRegistryId!',
                      'Expected:', siteRegistryId,
                      'Backend returned:', rulesRes.data.siteIdentifierRegistryId,
                      'Keeping expected value:', siteRegistryId
                    );
                    // Don't overwrite - siteRegistryId from getSite() is authoritative
                  } else {
                    setSiteIdentifierRegistryId(rulesRes.data.siteIdentifierRegistryId);
                  }
                }
                
                const siteRulesArr = Array.isArray(rulesRes.data.siteRules) ? rulesRes.data.siteRules : [];
                const vtContainers = (rulesRes.data as { virtualTenants?: unknown[] }).virtualTenants;
                const vtRuleCountMap = buildVirtualTenantRuleCountMap(vtContainers);
                const vtRuleIdsFromContainers = collectVirtualTenantRuleIdsFromContainers(vtContainers);
                const pathMatchResult = await resolveVirtualTenantRuleIdsByPathMatch(siteRulesArr, vts);
                const mergedVtRuleCountMap: Record<string, number> = { ...vtRuleCountMap };
                Object.entries(pathMatchResult.vtRuleCountMap).forEach(([vtId, count]) => {
                  mergedVtRuleCountMap[vtId] = Math.max(mergedVtRuleCountMap[vtId] || 0, count);
                });
                const vtRuleIds = new Set<number>([
                  ...vtRuleIdsFromContainers,
                  ...pathMatchResult.vtRuleIds,
                ]);
                const siteOnlyRules = siteRulesArr
                  .filter((rule) => !vtRuleIds.has(Number(rule.siteRuleId)))
                  .map(rule => enrichRuleWithCurrentSiteContext(rule));

                initialSiteRules = siteOnlyRules;
                initialVtRuleCountMap = mergedVtRuleCountMap;

                // Parent site rules table should only show site-level rules.
                setSiteRules(siteOnlyRules);
                
                // Fetch rule details for display (initial load)
                if (siteOnlyRules.length > 0) {
                  try {
                    const ids = siteOnlyRules.map((s: RuleSummary) => s.siteRuleId).filter(Boolean);
                    const results = await Promise.allSettled(ids.map((ruleId: number) => getRule(ruleId)));
                    const map: Record<number, RuleDetail> = {};
                    results.forEach((r, idx) => {
                      if (r.status === 'fulfilled') {
                        const ruleResponse = r.value;
                        if (ruleResponse?.success && ruleResponse.data) {
                          map[ids[idx]] = ruleResponse.data;
                        }
                      } else {
                        console.info(`[SiteDetails] Initial - getRule(${ids[idx]}) failed:`, (r as PromiseRejectedResult).reason);
                      }
                    });
                    setSiteRuleDetails(map);
                  } catch (e) {
                    console.info('[SiteDetails] Initial load rule details failed', e);
                  }
                }
              } else {
                setSiteRules([]);
              }
            } catch (rulesErr: unknown) {
              setSiteRules([]);
              setRulesError(rulesErr instanceof Error ? rulesErr.message : 'Failed to load rules');
            }
          } catch (rErr: unknown) {
            setRulesError(rErr instanceof Error ? rErr.message : 'Failed to load rules');
            setSiteRules([]);
          } finally {
            setRulesLoading(false);
          }
          // Enrich virtual tenants with their associated alarm paths and rule counts.
          try {
            if (Array.isArray(vts) && vts.length > 0) {
              const enriched = await enrichVirtualTenants(vts, initialSiteRules, initialVtRuleCountMap);
              if (mounted) {
                setVirtualTenants(enriched);
              }
            }
          } catch (e) {
            // non-fatal
            console.info('[SiteDetails] Failed to enrich virtual tenants', e);
          }
        } else {
          setError(res?.error?.message || 'Site not found');
        }
      } catch (e: unknown) {
        if (mounted) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (mounted) {setLoading(false);}
      }
    };
    void fetch();
    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }
    let mounted = true;

    const fetchSiteAlarms = async () => {
      setAlarmsLoading(true);
      try {
        const alarmsRes = await getAlarmsBySite(id, {
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          page: bureauPage,
          pageSize: bureauPageSize,
          sortBy: bureauSortColumn,
          sortDirection: bureauSortDirection,
        });
        const siteAlarms: BureauAlarm[] = alarmsRes?.data?.alarms || alarmsRes?.data?.bureauAlarms || [];
        const sortedSiteAlarms = sortBureauAlarmsForFallback(siteAlarms);

        if (!mounted) {
          return;
        }
        setBureauAlarms(sortedSiteAlarms);
        setAlarmsTotal(alarmsRes?.data?.totalRecords ?? siteAlarms.length);
        setBureauTotalPages(alarmsRes?.data?.totalPages ?? 1);
        setSelectedIds(prev => {
          const keys = Object.keys(prev).map(k => Number(k));
          const valid = new Set(sortedSiteAlarms.map(a => Number(a.bureauAlarmId || a.id || 0)).filter(Boolean));
          const next: Record<number, boolean> = {};
          for (const k of keys) {
            if (valid.has(k)) {next[k] = true;}
          }
          return next;
        });
      } catch (alarmsErr) {
        console.info('[SiteDetails] Error loading bureau alarms:', alarmsErr);
      } finally {
        if (mounted) {
          setAlarmsLoading(false);
        }
      }
    };

    void fetchSiteAlarms();
    return () => {
      mounted = false;
    };
  }, [id, alarmsRefreshCount, selectedCategory, bureauPage, bureauPageSize, bureauSortColumn, bureauSortDirection]);

  // Fetch stable per-category totals for the Site filter badges.
  // Keep this independent from current page/sort/category so counts do not
  // change when the user toggles filter options.
  useEffect(() => {
    let mounted = true;

    const fetchSiteCategoryCounts = async () => {
      const registryId = Number(id);
      if (!registryId || Number.isNaN(registryId)) {
        return;
      }

      try {
        const [activeNotAckRes, activeAckRes, notActiveNotAckRes] = await Promise.all([
          getAlarmsBySite(registryId, { category: 'active-not-ack', page: 1, pageSize: 1 }),
          getAlarmsBySite(registryId, { category: 'active-ack', page: 1, pageSize: 1 }),
          getAlarmsBySite(registryId, { category: 'not-active-not-ack', page: 1, pageSize: 1 }),
        ]);

        if (!mounted) {
          return;
        }

        const activeNotAckTotal = activeNotAckRes?.data?.totalRecords ?? 0;
        const activeAckTotal = activeAckRes?.data?.totalRecords ?? 0;
        const notActiveNotAckTotal = notActiveNotAckRes?.data?.totalRecords ?? 0;

        setSiteCategoryCounts({
          all: activeNotAckTotal + activeAckTotal + notActiveNotAckTotal,
          'active-not-ack': activeNotAckTotal,
          'active-ack': activeAckTotal,
          'not-active-not-ack': notActiveNotAckTotal,
        });
      } catch (countsErr) {
        console.info('[SiteDetails] Error loading site category counts:', countsErr);
      }
    };

    void fetchSiteCategoryCounts();

    return () => {
      mounted = false;
    };
  }, [id, alarmsRefreshCount]);

  // Auto-refresh when a bureau alarm notification arrives via SignalR
  useEffect(() => {
    return alarmAcknowledgedEvent.subscribe((payload) => {
      if (payload?.source === 'signalr' && payload?.eventType === 'BureauAlarmUpdated') {
        return;
      }
      setAlarmsRefreshCount(prev => prev + 1);
    });
  }, []);

  // Listen for rule lifecycle events from elsewhere (global Rules page) and refresh.
  useEffect(() => {
    const handleRuleLifecycleEvent = (eventName: string) => (e: Event) => {
      try {
        const changedSiteId = (e as CustomEvent<{ siteId?: number }>).detail?.siteId;
        const registryId = site?.id ?? id;
        if (changedSiteId && registryId && Number(changedSiteId) === Number(registryId)) {
          void refreshRules();
        }
      } catch (err) {
        console.info(`[SiteDetails] ${eventName} handler error`, err);
      }
    };

    const eventNames = ['rules:created', 'rules:updated', 'rules:deleted', 'rules:status-changed'];
    eventNames.forEach(eventName => {
      globalThis.addEventListener(eventName, handleRuleLifecycleEvent(eventName) as EventListener);
    });

    return () => {
      eventNames.forEach(eventName => {
        globalThis.removeEventListener(eventName, handleRuleLifecycleEvent(eventName) as EventListener);
      });
    };
  }, [site?.id, id, virtualTenants, refreshRules]);

  // Helper to enrich virtual tenants with inherited rule/path counts
  const enrichVirtualTenants = async (
    vts: VirtualTenantLike[],
    currentSiteRules: RuleSummary[],
    vtRuleCountMap: Record<string, number> = {}
  ): Promise<VirtualTenantLike[]> => {
    if (!Array.isArray(vts) || vts.length === 0) {return vts; }

    const toNumericPathId = (item: unknown): number | undefined => {
      if (typeof item === 'number') { return item; }
      if (typeof item === 'object' && item !== null) {
        const obj = item as Record<string, unknown>;
        const val = obj.alarmPathId ?? obj.id;
        if (typeof val === 'number') { return val; }
      }
      return undefined;
    };

    const vtResults = await Promise.allSettled(vts.map((vt) => (vt.id != null ? getVirtualTenantAlarmPaths(vt.id) : Promise.resolve({ success: true, data: [] as number[] }))));
    return vts.map((vt, idx: number) => {
      const res = vtResults[idx];
      let associated: number[] = [];
      if (res && res.status === 'fulfilled') {
        const val = res.value;
        if (val?.success && Array.isArray(val.data)) {
          associated = val.data
            .map(toNumericPathId)
            .filter((pathId): pathId is number => typeof pathId === 'number');
        }
      }

      // Backend doesn't return inheritRules, so we use this logic:
      // If VT has NO explicit alarm paths AND site has rules, assume it inherits
      const hasExplicitPaths = associated.length > 0;

      let finalRules: RuleSummary[] = [];
      let finalRuleCount = 0;

      const mappedRuleCount = resolveVirtualTenantRuleCount(vt, vtRuleCountMap);

      if (!hasExplicitPaths && Array.isArray(currentSiteRules) && currentSiteRules.length > 0 && mappedRuleCount === 0) {
        // No explicit paths + site has rules = inherit from parent site
        finalRules = currentSiteRules;
        finalRuleCount = currentSiteRules.length;
      } else {
        finalRuleCount = mappedRuleCount;
      }

      return {
        ...vt,
        associatedAlarmPaths: associated,
        ruleCount: finalRuleCount,
        inheritedRules: finalRules,
      };
    });
  };

  const anySelected = Object.values(selectedIds || {}).some(v => v === true);

  const handleClearFilter = () => {
    setSelectedCategory('active-not-ack');
    setSearchQuery('');
  };

  const isFilterActive = selectedCategory !== 'active-not-ack';

  const mapBureauSortColumnToServer = (column: string): string => {
    switch (column) {
      case 'last': return 'lastOccurrenceAt';
      case 'path': return 'pathToAlarm';
      case 'sourceAlarmState': return 'bureauAlarmState';
      case 'ack': return 'localAck';
      default: return column;
    }
  };

  const sortBureauAlarmsForFallback = (alarms: BureauAlarm[]): BureauAlarm[] => {
    if (!Array.isArray(alarms) || alarms.length <= 1) {
      return alarms;
    }

    const key = bureauSortColumn;
    if (!['siteName', 'path', 'pathToAlarm', 'last', 'lastOccurrenceAt'].includes(key)) {
      return alarms;
    }

    const copy = [...alarms];
    copy.sort((a, b) => {
      let result = 0;

      if (key === 'siteName') {
        const av = String(a.siteName || a.siteIdentifier || '').toLowerCase();
        const bv = String(b.siteName || b.siteIdentifier || '').toLowerCase();
        result = av.localeCompare(bv);
      } else if (key === 'path' || key === 'pathToAlarm') {
        const av = String(a.pathToAlarm || a.path || a.alarmIdentifier || '').toLowerCase();
        const bv = String(b.pathToAlarm || b.path || b.alarmIdentifier || '').toLowerCase();
        result = av.localeCompare(bv);
      } else if (key === 'last' || key === 'lastOccurrenceAt') {
        const av = a.lastOccurrenceAt ? new Date(a.lastOccurrenceAt).getTime() : 0;
        const bv = b.lastOccurrenceAt ? new Date(b.lastOccurrenceAt).getTime() : 0;
        result = av - bv;
      }

      if (bureauSortDirection === 'asc') {
        return result;
      }
      return -result;
    });

    return copy;
  };

  const handleBureauSort = (column: string) => {
    const mappedColumn = mapBureauSortColumnToServer(column);
    let nextDirection: 'asc' | 'desc' = 'asc';
    if (bureauSortColumn === mappedColumn) {
      if (bureauSortDirection === 'asc') {
        nextDirection = 'desc';
      } else {
        nextDirection = 'asc';
      }
    }
    setBureauSortColumn(mappedColumn);
    setBureauSortDirection(nextDirection);
    setBureauPage(1);
  };

  // Apply search and category filter to bureau alarms
  const filteredBureauAlarms = useMemo(() => {
    let result = [...bureauAlarms];

    // Search query filter
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const searchableFields = [
          item.siteName,
          item.siteIdentifier,
          item.pathToAlarm,
          item.path,
          item.alarmIdentifier,
          item.bureauRecipientName,
          item.bureauName,
          item.bureauEndpoint,
          item.bureauAlarmState,
          String(item.priority || ''),
        ];
        
        return searchableFields.some(field => 
          field && String(field).toLowerCase().includes(query)
        );
      });
    }

    return result;
  }, [bureauAlarms, searchQuery]);

  const selectedAlarms = useMemo(() => {
    return filteredBureauAlarms.filter(alarm => !!selectedIds[(alarm.bureauAlarmId || alarm.id) ?? 0]);
  }, [filteredBureauAlarms, selectedIds]);

  const hasUnacknowledgedSelected = useMemo(() => {
    return selectedAlarms.some(a => !a.localAck);
  }, [selectedAlarms]);

  const acknowledgeSelected = () => {
    if (!selectedAlarms || selectedAlarms.length === 0) {
      return;
    }
    setBulkMode('acknowledge');
  };

  const handleBulkCopy = async () => {
    const selected = selectedAlarms;
    if (!selected || selected.length === 0) {
      return;
    }

    try {
      const alarmsData = selected.map((alarm: BureauAlarm) => {
        const sections = getAlarmSections(alarm);
        return { text: alarmSectionsToText(sections), html: alarmSectionsToHtml(sections) };
      });

      const final = alarmsData.map(d => d.text).join('\n\n---\n\n');
      const finalHtml = alarmsData.map(d => d.html).join('<hr/>');

      // Try Clipboard API with text/html and text/plain; fallback to textarea/contentEditable execCommand copy
      try {
        const clipboardNav = navigator as ClipboardNavigator;
        const clipboardGlobal = globalThis as ClipboardGlobal;
        if (clipboardNav.clipboard?.write && clipboardGlobal.ClipboardItem) {
          const blobPlain = new Blob([final], { type: 'text/plain' });
          const blobHtml = new Blob([finalHtml], { type: 'text/html' });
          const item = new clipboardGlobal.ClipboardItem({ 'text/plain': blobPlain, 'text/html': blobHtml });
          await clipboardNav.clipboard.write([item]);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(final);
        } else {
          const div = document.createElement('div');
          div.style.position = 'fixed';
          div.style.left = '-9999px';
          div.contentEditable = 'true';
          div.innerHTML = finalHtml;
          document.body.appendChild(div);
          const range = document.createRange();
          range.selectNodeContents(div);
          const sel = globalThis.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
          if ('execCommand' in document) {
            // eslint-disable-next-line deprecation
            document.execCommand('copy');
          }
          sel?.removeAllRanges();
          div.remove();
        }
      } catch (e) {
        // last resort: plain text
        try {
          await navigator.clipboard.writeText(final);
        } catch (_) {
          const ta = document.createElement('textarea');
          ta.value = final;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          if ('execCommand' in document) {
            // eslint-disable-next-line deprecation
            document.execCommand('copy');
          }
          document.body.removeChild(ta);
        }
      }

      setAckSuccessMessage(`Copied ${selected.length} alarm(s) to clipboard`);
      setTimeout(() => setAckSuccessMessage(null), 4000);
    } catch (err) {
      console.info('Failed to copy selected alarms', err);
      setAckErrorMessage('Failed to copy selected alarms');
      setTimeout(() => setAckErrorMessage(null), 4000);
    }
  };

  const handleBulkAcknowledge = async (alarms: BureauAlarm[]) => {
    const profileResponse = await getProfile();
    if (!profileResponse?.success || !profileResponse.data?.id) {
      throw new Error('Unable to retrieve user profile');
    }

    const acknowledgedBy = profileResponse.data.id;
    const userName = userProfile?.preferred_username || userProfile?.email || userProfile?.name || 'Unknown User';

    const payload = { acknowledgedBy, userName };

    let successCount = 0;
    const errorItems: Array<{ id: number | string; message: string }> = [];

    for (const alarm of alarms) {
      const alarmId = alarm.bureauAlarmId || alarm.id; // Ensure alarm is of type BureauAlarm
      if (!alarmId) {continue;}
      setActionInFlight(String(alarmId));
      try {
        const response = await localAcknowledge(alarmId, payload);
        if (response?.success) {
          successCount++;
        } else {
          console.info(`Failed to acknowledge alarm ${alarmId}:`, response?.error);
          errorItems.push({ id: alarmId, message: response?.error?.message || 'Unknown error' });
        }
      } catch (err: unknown) {
        console.info(`Error acknowledging alarm ${alarmId}:`, err);
        errorItems.push({ id: alarmId, message: err instanceof Error ? err.message : String(err) });
      }
    }

    setActionInFlight(null);
    setBulkMode(null);
    setSelectedIds({});

    // Optimistically update the local state for acknowledged alarms
    if (successCount > 0) {
      const acknowledgedIds = new Set(
        alarms
          .filter(a => !errorItems.some(e => e.id === (a.bureauAlarmId || a.id)))
          .map(a => a.bureauAlarmId || a.id)
          .filter(Boolean)
      );
      
      setBureauAlarms(prev => 
        prev.map(alarm => {
          const alarmId = alarm.bureauAlarmId || alarm.id;
          if (alarmId && acknowledgedIds.has(alarmId)) {
            return { ...alarm, localAck: true };
          }
          return alarm;
        })
      );
    }

    // Show status messages
    if (successCount > 0) {
      const successfulAlarms = alarms.filter(a => !errorItems.some(e => e.id === (a.bureauAlarmId || a.id)));
      const siteNames = [...new Set(successfulAlarms.map(a => a.siteName || a.siteIdentifier || String(a.bureauAlarmId || a.id)))].join(', ');
      setAckSuccessMessage(`Acknowledged ${successCount} alarm(s): ${siteNames}`);
      setTimeout(() => setAckSuccessMessage(null), TOAST_SUCCESS_MS);
    }
    if (errorItems.length > 0) {
      const msgs = errorItems.map(e => `ID ${e.id}: ${e.message}`).slice(0, 5).join(' | ');
      setAckErrorMessage(`Failed to acknowledge ${errorItems.length} alarm(s): ${msgs}`);
      setTimeout(() => setAckErrorMessage(null), TOAST_LONG_MS);
    }

    // Refresh from backend to ensure we're in sync (site endpoint returns all alarms)
    setTimeout(() => setAlarmsRefreshCount(c => c + 1), 300);

    // Emit event to notify Sidebar to refresh badge counts
    if (successCount > 0) {
      setTimeout(() => {
        alarmAcknowledgedEvent.emit();
      }, 500);
    }
  };

  // Rule management helper functions
  async function refreshRules() {
    if (!site) {return;}
    // CRITICAL: Use URL parameter as the canonical registry ID
    // This ensures we're fetching rules for the site the user navigated to
    const identifier = id; // Use URL parameter directly
    if (!identifier) {return;}
    setRulesLoading(true);
    setRulesError(null);
    try {
      const rulesRes = await listSiteRules(identifier);
      if (rulesRes?.success && rulesRes.data) {
        const fetchedRules = rulesRes.data.siteRules || [];
        const vtContainers = (rulesRes.data as { virtualTenants?: unknown[] }).virtualTenants;
        const vtRuleCountMap = buildVirtualTenantRuleCountMap(vtContainers);
        const vtRuleIdsFromContainers = collectVirtualTenantRuleIdsFromContainers(vtContainers);
        const pathMatchResult = await resolveVirtualTenantRuleIdsByPathMatch(fetchedRules, virtualTenants);
        const mergedVtRuleCountMap: Record<string, number> = { ...vtRuleCountMap };
        Object.entries(pathMatchResult.vtRuleCountMap).forEach(([vtId, count]) => {
          mergedVtRuleCountMap[vtId] = Math.max(mergedVtRuleCountMap[vtId] || 0, count);
        });
        const vtRuleIds = new Set<number>([
          ...vtRuleIdsFromContainers,
          ...pathMatchResult.vtRuleIds,
        ]);
                const siteOnlyRules = fetchedRules
                  .filter((rule) => !vtRuleIds.has(Number(rule.siteRuleId)))
                  .map(rule => enrichRuleWithCurrentSiteContext(rule));
        setSiteRules(siteOnlyRules);
        // Update both IDs if available
        if (rulesRes.data.siteHierarchyId) {
          setSiteHierarchyId(rulesRes.data.siteHierarchyId);
        }
        if (rulesRes.data.siteIdentifierRegistryId) {
          setSiteIdentifierRegistryId(rulesRes.data.siteIdentifierRegistryId);
        }
        
        // After refreshing summaries, load details for display immediately (use fresh fetched data)
        try {
          const ids = siteOnlyRules.map((s: RuleSummary) => s.siteRuleId).filter(Boolean);
          console.info('[SiteDetails] Fetching details for rule IDs:', ids);
          if (ids.length > 0) {
            const results = await Promise.allSettled(ids.map((id: number) => getRule(id)));
            const map: Record<number, RuleDetail> = {};
            results.forEach((r, idx) => {
              if (r.status === 'fulfilled') {
                const ruleResponse = r.value;
                if (ruleResponse?.success && ruleResponse.data) {
                  map[ids[idx]] = ruleResponse.data;
                }
              } else {
                console.info(`[SiteDetails] getRule(${ids[idx]}) failed:`, (r as PromiseRejectedResult).reason);
              }
            });
            setSiteRuleDetails(map);
          }
        } catch (e) {
          // non-fatal
          console.info('[SiteDetails] load rule details failed', e);
        }
        
        // Refresh VT rule counts using the latest site-rules response context.
        if (Array.isArray(virtualTenants) && virtualTenants.length > 0) {
          try {
            const enrichedVts = await enrichVirtualTenants(virtualTenants, siteOnlyRules, mergedVtRuleCountMap);
            setVirtualTenants(enrichedVts);
          } catch (vtErr) {
            console.info('[SiteDetails] Failed to refresh VT counts during rules refresh', vtErr);
          }
        }
        
        // Fallback: if listSiteRules returned no rules, try fetching all rules and filter client-side
        if ((!fetchedRules || fetchedRules.length === 0) && (identifier || siteHierarchyId)) {
          try {
            console.info('[SiteDetails] No rules returned by listSiteRules; falling back to listAllRules filter');
            
            const allRes = await listAllRules();
            if (Array.isArray(allRes?.data)) {
              const candidates = allRes.data.filter((r: RuleSummary & { siteRegistryId?: number; siteId?: number; shId?: number }) => {
                // Match by siteIdentifierRegistryId or siteHierarchyId
                const reg = r.siteIdentifierRegistryId ?? r.siteRegistryId ?? r.siteId;
                const sh = r.siteHierarchyId ?? r.shId;
                if (reg && identifier && Number(reg) === Number(identifier)) {
                  return true;
                }
                if (sh && siteHierarchyId && Number(sh) === Number(siteHierarchyId)) {
                  return true;
                }
                return false;
              });
              if (candidates.length > 0) {
                // Normalize to RuleSummary shape expected by siteRules
                const normalized = candidates.map((c: RuleSummary) => enrichRuleWithCurrentSiteContext({
                  siteRuleId: c.siteRuleId,
                  siteHierarchyId: c.siteHierarchyId,
                  siteIdentifierRegistryId: c.siteIdentifierRegistryId,
                  siteName: c.siteName,
                  siteIdentifier: c.siteIdentifier,
                  ruleName: c.ruleName,
                  isActive: c.isActive,
                  distributionGroupCount: c.distributionGroupCount,
                  attachmentGroupCount: c.attachmentGroupCount,
                  createdAt: c.createdAt
                } as RuleSummary));
                const filteredNormalized = normalized.filter((rule: RuleSummary) => !vtRuleIds.has(Number(rule.siteRuleId)));
                setSiteRules(filteredNormalized);
              }
            }
          } catch (e) {
            console.info('[SiteDetails] listAllRules fallback failed', e);
          }
        }
      } else {
        // If the response isn't successful, keep existing rules or set empty
        setSiteRules(prev => prev || []);
      }
    } catch (err: unknown) {
      setRulesError(err instanceof Error ? err.message : 'Failed to refresh rules');
      // Don't clear the rules on error, keep showing what we have
    } finally {
      setRulesLoading(false);
    }
  };

  const handleEditRule = async (ruleId: number) => {
    setEditingRuleId(ruleId);
    setSuccessMessage(null);
    setErrorMessage(null);
    setIsCreateModalOpen(false);
    
    try {
      const res = await getRule(ruleId);
      if (res?.success && res.data) {
        const rule = enrichRuleWithCurrentSiteContext(res.data);
        const routeSiteRegistryId = Number(id);
        const resolvedSiteRegistryId = !Number.isNaN(routeSiteRegistryId) && routeSiteRegistryId > 0
          ? routeSiteRegistryId
          : (site?.id ? Number(site.id) : NaN);

        let resolvedVirtualTenantIds: string[] = [];
        try {
          if (!Number.isNaN(resolvedSiteRegistryId) && resolvedSiteRegistryId > 0) {
            const siteRulesRes = await listSiteRules(resolvedSiteRegistryId);
            const vtContainers = siteRulesRes?.success && siteRulesRes.data?.virtualTenants
              ? siteRulesRes.data.virtualTenants
              : [];

            if (Array.isArray(vtContainers) && vtContainers.length > 0) {
              resolvedVirtualTenantIds = vtContainers
                .filter(vt => Array.isArray(vt.rules) && vt.rules.some(vtRule => extractRuleIdFromUnknown(vtRule) === Number(ruleId)))
                .map(vt => Number(vt.siteHierarchyId))
                .filter(vtId => !Number.isNaN(vtId) && vtId > 0)
                .map(String);
            }
          }

          if (resolvedVirtualTenantIds.length === 0 && Array.isArray(virtualTenants) && virtualTenants.length > 0) {
            const rulePathSet = new Set(extractRuleAlarmPathIds(rule.alarmPaths));
            if (rulePathSet.size > 0) {
              const subsetMatches: string[] = [];
              for (const vt of virtualTenants) {
                const vtId = Number(vt.id);
                if (Number.isNaN(vtId) || vtId <= 0) {
                  continue;
                }
                const vtPathRes = await getVirtualTenantAlarmPaths(vtId);
                const vtPathSet = new Set(extractVirtualTenantAlarmPathIds(vtPathRes?.data));
                if (vtPathSet.size === 0 || rulePathSet.size > vtPathSet.size) {
                  continue;
                }
                let isSubsetMatch = true;
                for (const pathId of rulePathSet) {
                  if (!vtPathSet.has(pathId)) {
                    isSubsetMatch = false;
                    break;
                  }
                }
                if (isSubsetMatch) {
                  subsetMatches.push(String(vtId));
                }
              }

              if (subsetMatches.length === 1) {
                resolvedVirtualTenantIds = subsetMatches;
              }
            }
          }

          if (resolvedVirtualTenantIds.length > 0 && Array.isArray(virtualTenants) && virtualTenants.length > 0) {
            const availableVtIdSet = new Set(
              virtualTenants
                .map(vt => Number(vt.id))
                .filter(vtId => !Number.isNaN(vtId) && vtId > 0)
                .map(String)
            );
            resolvedVirtualTenantIds = resolvedVirtualTenantIds.filter(vtId => availableVtIdSet.has(vtId));
          }
        } catch (vtResolveErr) {
          console.info('[SiteDetails] Failed to resolve VT tags for rule edit', vtResolveErr);
        }

        const ruleFormData: RuleFormData = {
          ruleName: rule.ruleName || '',
          isActive: rule.isActive ?? true,
          bindPathPriority: rule.bindPathPriority ?? true,
          distributionGroupIds: (rule.distributionGroups || []).map(dg => String(dg.distributionGroupId)).join(','),
          attachmentGroupIds: (rule.attachmentGroups || []).map(ag => String(ag.attachmentGroupId)).join(','),
          appliesToSiteIds: !Number.isNaN(resolvedSiteRegistryId) && resolvedSiteRegistryId > 0 ? String(resolvedSiteRegistryId) : '',
          appliesToVirtualTenantIds: resolvedVirtualTenantIds.join(','),
          filteringPaths: extractRuleAlarmPathIds(rule.alarmPaths).join(','),
          filteringPathLabelsById: extractRuleAlarmPathLabelsById(rule.alarmPaths),
          filteringPriorities: rule.priorities || [],
          hideRtn: rule.hideRtn ?? false,
          rtnExceptions: (rule.rtnExceptions || []).join(','),
          isUltraCritical: rule.isUltraCritical ?? false
        };
        setEditingRuleData(ruleFormData);
        setIsEditModalOpen(true);
      } else {
        setErrorMessage(res?.error?.message || 'Failed to load rule details');
      }
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const handleUpdateRule = async (ruleId: number, ruleData: RuleFormData) => {
    if (!isEditFormValid) {
      setShowEditErrorSummary(true);
      return;
    }
    
    setSuccessMessage(null);
    setErrorMessage(null);
    setShowEditErrorSummary(false);
    
    try {
      const pathIds = (ruleData.filteringPaths || '')
        .split(',')
        .map((s: string) => Number(s.trim()))
        .filter((pathId: number) => Number.isFinite(pathId) && pathId > 0);

      const payload = {
        ruleName: ruleData.ruleName || `Rule ${ruleId}`,
        isActive: !!ruleData.isActive,
        isUltraCritical: !!ruleData.isUltraCritical,
        bindPathPriority: !!ruleData.bindPathPriority,
        filteringCriteria: {
          pathIds,
          paths: pathIds.map(pathId => String(pathId)),
          priorities: Array.isArray(ruleData.filteringPriorities) ? ruleData.filteringPriorities : [],
          classes: [],
          patterns: []
        },
        distributionGroupIds: (ruleData.distributionGroupIds || '').split(',').map((s: string) => Number(s.trim())).filter(Boolean),
        attachmentGroupIds: (ruleData.attachmentGroupIds || '').split(',').map((s: string) => Number(s.trim())).filter(Boolean),
        behavior: {
          hideRtn: !!ruleData.hideRtn,
          rtnExceptions: (ruleData.rtnExceptions || '').split(',').map((s: string) => s.trim()).filter(Boolean)
        }
      };
      
      const res = await updateRule(ruleId, payload);
      if (res?.success) {
        setSuccessMessage('Rule updated successfully');
        
        // Immediately fetch the updated rule details to ensure UI reflects changes
        try {
          const detailsRes = await getRule(ruleId);
          if (detailsRes?.success && detailsRes.data) {
            const updatedDetails = enrichRuleWithCurrentSiteContext(detailsRes.data);
            console.info('[SiteDetails] Updated rule details:', detailsRes.data);
            setSiteRuleDetails(prev => ({
              ...prev,
              [ruleId]: updatedDetails
            }));
          }
        } catch (err) {
          console.info('[SiteDetails] Failed to fetch updated rule details', err);
        }

        try {
          if (id) {
            const siteRes = await getSite(id);
            if (siteRes?.success && siteRes.data) {
              setSite(siteRes.data as unknown as SiteDetail);
              const refreshedVirtualTenants = toVirtualTenants(siteRes.data.children);
              setVirtualTenants(await enrichVirtualTenants(refreshedVirtualTenants, siteRulesRef.current || []));
            }
          }
        } catch (err) {
          console.info('[SiteDetails] Failed to refresh site context after rule update', err);
        }
        
        // Also refresh the full rules list to ensure summary is current
        await refreshRules();
        
        setEditingRuleId(null);
        setEditingRuleData(null);
        setIsEditModalOpen(false);
      } else {
        setErrorMessage(res?.error?.message || 'Failed to update rule');
      }
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDeactivateRule = async (ruleId: number) => {
    const confirmed = await showConfirmModal('Are you sure you want to deactivate this rule?', 'Deactivate Rule');
    if (!confirmed) return;
    try {
      const res = await patchRuleStatus(ruleId, { isActive: false });
      if (res?.success) {
        // Optimistically update the state
        setSiteRules(prev => prev?.map(r => 
          r.siteRuleId === ruleId ? { ...r, isActive: false } : r
        ) || []);
        setSuccessMessage('Rule deactivated successfully');
      } else {
        setErrorMessage(res?.error?.message || 'Failed to deactivate rule');
      }
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
      // Refresh to get accurate state after error
      await refreshRules();
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    const rule = siteRules?.find(r => r.siteRuleId === ruleId);
    const confirmMsg = rule?.isActive
      ? 'Are you sure you want to deactivate this rule?'
      : 'Are you sure you want to delete this rule? This action cannot be undone.';
    const confirmed = await showConfirmModal(confirmMsg, rule?.isActive ? 'Deactivate Rule' : 'Delete Rule');
    if (!confirmed) {
      return;
    }
    try {
      let res;
      if (rule?.isActive) {
        res = await patchRuleStatus(ruleId, { isActive: false });
        if (res?.success) {
          // Optimistically update the state
          setSiteRules(prev => prev?.map(r => 
            r.siteRuleId === ruleId ? { ...r, isActive: false } : r
          ) || []);
          setSuccessMessage('Rule deactivated successfully');
        }
      } else {
        // Actually delete inactive rules
        res = await deleteRule(ruleId);
        console.info('[SiteDetails] Delete response:', res);
        if (res?.success) {
          // Optimistically remove from state
          setSiteRules(prev => prev?.filter(r => r.siteRuleId !== ruleId) || []);
          setSuccessMessage('Rule deleted');
        }
      }
      
      if (!res?.success) {
        console.info('[SiteDetails] Delete/deactivate failed:', res?.error);
        setErrorMessage(res?.error?.message || 'Failed to deactivate/delete rule');
      }
    } catch (err: unknown) {
      console.info('[SiteDetails] Delete/deactivate exception:', err);
      setErrorMessage(err instanceof Error ? err.message : String(err));
      // Refresh to get accurate state after error
      await refreshRules();
    }
  };

  const handleActivateRule = async (ruleId: number) => {
    const confirmed = await showConfirmModal('Are you sure you want to activate this rule?', 'Activate Rule');
    if (!confirmed) return;
    try {
      const res = await patchRuleStatus(ruleId, { isActive: true });
      if (res?.success) {
        // Optimistically update the state
        setSiteRules(prev => prev?.map(r => 
          r.siteRuleId === ruleId ? { ...r, isActive: true } : r
        ) || []);
        setSuccessMessage('Rule activated successfully');
      } else {
        setErrorMessage(res?.error?.message || 'Failed to activate rule');
      }
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
      // Refresh to get accurate state after error
      await refreshRules();
    }
  };

  const handleCreateRules = async () => {
    if (!isCreateFormValid) {
      setShowCreateErrorSummary(true);
      return;
    }
    
    if (!newRules || newRules.length === 0) {
      setErrorMessage('No rules to create');
      return;
    }
    
    if (!siteIdentifierRegistryId) {
      setErrorMessage('Cannot create rules: Site registry ID is not available. Please refresh the page.');
      return;
    }
    
    
    
    // If we don't have the hierarchy ID yet, query for it now
    let effectiveHierarchyId = siteHierarchyId;
    if (!effectiveHierarchyId) {
      try {
        const rulesRes = await listSiteRules(siteIdentifierRegistryId);
        if (rulesRes?.success && rulesRes.data?.siteHierarchyId) {
          effectiveHierarchyId = rulesRes.data.siteHierarchyId;
          setSiteHierarchyId(effectiveHierarchyId);
        } else {
          setErrorMessage('Cannot create rules: Site not yet configured in rules system. Please create the first rule from the Rules page.');
          setIsCreatingRules(false);
          return;
        }
      } catch (err) {
        setErrorMessage('Cannot create rules: Failed to query site configuration.');
        setIsCreatingRules(false);
        return;
      }
    }
    
    
    setIsCreatingRules(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    
    const errors: string[] = [];
    let successCount = 0;
    
    for (let i = 0; i < newRules.length; i++) {
      const ruleData = newRules[i];
      try {
        const pathIds = (ruleData.filteringPaths || '')
          .split(',')
          .map((s: string) => Number(s.trim()))
          .filter((pathId: number) => Number.isFinite(pathId) && pathId > 0);

        const payload: CreateRuleRequest = {
          siteIdentifierRegistryId: siteIdentifierRegistryId, // URL parameter (site user navigated to)
          siteHierarchyId: effectiveHierarchyId, // Queried from backend
          entityType: 'Site',
          ruleName: ruleData.ruleName || `Rule for site ${id}`,
          isActive: !!ruleData.isActive,
          isUltraCritical: !!ruleData.isUltraCritical,
          bindPathPriority: !!ruleData.bindPathPriority,
          filteringCriteria: {
            pathIds,
            paths: pathIds.map(pathId => String(pathId)),
            priorities: Array.isArray(ruleData.filteringPriorities) ? ruleData.filteringPriorities : [],
            classes: [],
            patterns: []
          },
          distributionGroupIds: (ruleData.distributionGroupIds || '').split(',').map((s: string) => Number(s.trim())).filter(Boolean),
          attachmentGroupIds: (ruleData.attachmentGroupIds || '').split(',').map((s: string) => Number(s.trim())).filter(Boolean),
          behavior: {
            hideRtn: !!ruleData.hideRtn,
            rtnExceptions: (ruleData.rtnExceptions || '').split(',').map((s: string) => s.trim()).filter(Boolean)
          }
        };
        
        const res = await createRule(payload);
        
        if (res?.success) {
          successCount++;
        } else {
          errors.push(`Rule ${i + 1}: ${res?.error?.message || 'Failed'}`);
        }
      } catch (err: unknown) {
        errors.push(`Rule ${i + 1}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    
    
    if (successCount > 0) {
      // Refresh rules to get the newly created rules with all their details
      await refreshRules();
      
      // Close modal and show success after refresh completes
      setSuccessMessage(`Successfully created ${successCount} rule(s)`);
      setNewRules([]);
      setIsCreateModalOpen(false);
      setShowCreateErrorSummary(false);
      setIsCreatingRules(false);
    } else {
      setIsCreatingRules(false);
    }
    
    if (errors.length > 0) {
      setErrorMessage(`Failed to create ${errors.length} rule(s): ${errors.join(' | ')}`);
    }
  };

  // Call useBreadcrumbs before any conditional returns (Rules of Hooks)
  const breadcrumbs = useBreadcrumbs({
    customerId: site?.customerId,
    customerName: site?.customerName,
    siteId: id,
    siteName: site?.siteName ?? site?.name ?? site?.displayName ?? site?.siteIdentifier,
  });

  if (loading || error) {
    return (
      <main>
        <div className="container">
          <section className='page'>
            <h2 className="heading__title heading__title--building">{loading ? 'Loading site…' : 'Site details'}</h2>
            <DateTimeDisplay />
          </section>
          <section className="page-section">
            {loading && <LoadingSpinner message="Loading site…" />}
              {error && (
                <StatusMessage kind="error" onClose={() => setError(null)}>
                  {error}
                </StatusMessage>
              )}
          </section>
        </div>
      </main>
    );
  }

  if (!site) return (
    <main>
      <div className="container">
        <div className="page-section">
          <h2>Site Not Found</h2>
          <p>The requested site could not be found.</p>
          <button className="btn btn--secondary" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>
      <Modal
        isOpen={confirmModalOpen}
        title={confirmModalTitle}
        headerClass="modal__header--danger"
        content={<ModalContent heading={confirmModalTitle} message={confirmModalMessage} />}
        onConfirm={() => {
          setConfirmModalOpen(false);
          confirmResolverRef.current?.(true);
        }}
        onCancel={() => {
          setConfirmModalOpen(false);
          confirmResolverRef.current?.(false);
        }}
      />
    </main>
  );

  const matchedSiteCategory = availableCategories.find(category => category.id === site.siteCategoryId);
  const siteCategoryLabel = site.siteCategoryName?.trim() || matchedSiteCategory?.name;
  const hasSiteCategory = Boolean(siteCategoryLabel);

  return (
    <main>
      <div className="container">
        
        <section className='page'>
          <h2 className="heading__title heading__title--building">{site.siteName ?? site.name ?? site.displayName ?? site.siteIdentifier}</h2>
          <DateTimeDisplay />
        </section>

        <Breadcrumbs items={breadcrumbs} />

        <>
            <BulkActionToolbar visible={anySelected && !bulkMode}>
              {hasUnacknowledgedSelected && (
                <button type="button" className="btn btn__toolbar--approve" onClick={acknowledgeSelected} disabled={!anySelected}>
                  Acknowledge Selected
                </button>
              )}
              <button type="button" className="btn btn__toolbar--copy" onClick={handleBulkCopy} disabled={!anySelected}>
                Copy Selected
              </button>
            </BulkActionToolbar>

            {/* Acknowledge-specific status messages (appear above the bureau table) */}
            {ackSuccessMessage && (
              <StatusMessage kind="success" onClose={() => setAckSuccessMessage(null)}>
                {ackSuccessMessage}
              </StatusMessage>
            )}
            {ackErrorMessage && (
              <StatusMessage kind="error" onClose={() => setAckErrorMessage(null)}>
                {ackErrorMessage}
              </StatusMessage>
            )}

            {/* Filter toolbar */}
            <section className="alarm-content mb-2">
              <div className="toolbar-content pt-1 pb-1">
                <div className="toolbar-content__left">
                  <h3 className="mb-1">CSH - {getBureauCategoryLabel(selectedCategory)}</h3>
                  <div className="toolbar-content__search">
                    <SearchQuery
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                    />
                  </div>
                </div>

                <div className="toolbar-content__right">
                  <div className="alarm-controls">
                    <div className="filter-container">
                      <FilterBtn 
                        onClick={() => setIsFilterOpen(!isFilterOpen)} 
                        isActive={isFilterActive} 
                        isDropdownOpen={isFilterOpen} 
                      />

                      <BureauFilter
                        isOpen={isFilterOpen}
                        onClose={() => setIsFilterOpen(false)}
                        selectedCategory={selectedCategory}
                        onCategoryChange={(cat) => {
                          setSelectedCategory(cat);
                          setBureauPage(1);
                        }}
                        categoryCounts={siteCategoryCounts}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-2">
              <BureauCategoryTable 
                filteredData={filteredBureauAlarms}
                totalCount={siteCategoryCounts[selectedCategory] ?? alarmsTotal}
                selectedIds={selectedIds}
                onToggleSelect={(id: number) => setSelectedIds(prev => ({ ...prev, [id]: !prev[id] }))}
                onToggleSelectAll={() => {
                  const allSelected = filteredBureauAlarms.every(item => selectedIds[(item.bureauAlarmId || item.id) ?? 0]);
                  if (allSelected) {
                    setSelectedIds({});
                  } else {
                    const newSelected: Record<number, boolean> = {};
                    filteredBureauAlarms.forEach(item => {
                      const id = (item.bureauAlarmId || item.id) ?? 0;
                      if (id) newSelected[id] = true;
                    });
                    setSelectedIds(newSelected);
                  }
                }}
                onAcknowledged={(count?: number, acknowledgedAlarmIds?: number[]) => {
                  // Optimistically update the local state immediately
                  if (acknowledgedAlarmIds && acknowledgedAlarmIds.length > 0) {
                    const ackSet = new Set(acknowledgedAlarmIds);
                    setBureauAlarms(prev => 
                      prev.map(alarm => {
                        const alarmId = alarm.bureauAlarmId || alarm.id;
                        if (alarmId && ackSet.has(alarmId)) {
                          return { ...alarm, localAck: true };
                        }
                        return alarm;
                      })
                    );
                  }
                  
                  if (acknowledgedAlarmIds && acknowledgedAlarmIds.length > 0) {
                    const ackSet = new Set(acknowledgedAlarmIds);
                    const ackedAlarms = filteredBureauAlarms.filter(a => ackSet.has((a.bureauAlarmId || a.id) as number));
                    const siteNames = [...new Set(ackedAlarms.map(a => a.siteName || a.siteIdentifier || String(a.bureauAlarmId || a.id)))].join(', ');
                    setAckSuccessMessage(`Acknowledged ${acknowledgedAlarmIds.length} alarm(s): ${siteNames}`);
                  } else {
                    setAckSuccessMessage(`Acknowledged ${count ?? 1} alarm(s)`);
                  }
                  setTimeout(() => setAckSuccessMessage(null), TOAST_SUCCESS_MS);
                  // Refresh from backend using site-specific endpoint (includes all alarms)
                  setTimeout(() => setAlarmsRefreshCount(c => c + 1), 300);
                }}
                disableSiteLink={true}
                sortColumn={bureauSortColumn === 'lastOccurrenceAt' ? 'last' : bureauSortColumn}
                sortOrder={bureauSortDirection}
                onSort={handleBureauSort}
                onResetSort={() => {
                  setBureauSortColumn('lastOccurrenceAt');
                  setBureauSortDirection('desc');
                  setBureauPage(1);
                }}
              />
              {bureauTotalPages > 1 && (
                <Pagination
                  currentPage={bureauPage}
                  totalPages={bureauTotalPages}
                  onPageChange={setBureauPage}
                />
              )}
            </section>
        </>

        <section className="alarm-content mb-2 p-1">
          <div className="alarm-content--heading">
            <h3 className='heading__title heading__title--segment-building mt-0 mb-1'>Site Information</h3>
            {hasSiteCategory && (
              <div className="site-category-assignment" style={{ alignSelf: 'flex-end' }}>
                <span className="site-category-assignment__prefix">Category:</span>
                <div className="site-category-tag" title={siteCategoryLabel}>
                  <span className="site-category-tag__label">{siteCategoryLabel}</span>
                </div>
              </div>
            )}
          </div>
          <div className="details-grid">
            <div><strong>Registry ID</strong></div><div>{site.id}</div>
            <div><strong>Site Name</strong></div><div>{site.siteName ?? site.name ?? site.displayName ?? site.siteIdentifier}</div>
            <div><strong>Site ID</strong></div><div>{site.siteGuid ?? site.siteGUID ?? site.guid ?? '—'}</div>
            <div><strong>Site Identifier</strong></div><div>{site.siteIdentifier}</div>
          </div>
        </section>

        <section className="alarm-content mb-2 p-1">
          <h3 className='heading__title heading__title--info mt-0 mb-1'>System Details</h3>
          <div className="details-grid">
            <div><strong>Customer</strong></div><div>{site.customerName ?? site.customerId ?? '—'}</div>
            <div><strong>Status Description</strong></div><div>{site.statusDescription ?? '—'}</div>
            <div><strong>Site Status</strong></div><div>{site.registryStatus ?? site.status ?? '—'}</div>
            <div><strong>System Type</strong></div><div>{site.systemTypeName ?? '—'}</div>
            <div><strong>Unique Alarm Paths</strong></div><div>{pathsCount != null ? pathsCount : '—'}</div>
            <div><strong>Region</strong></div><div>{site.region ?? '—'}</div>
          </div>
        </section>

        <section className="alarm-content mb-2 p-1">
          <h3 className='heading__title heading__title--info mt-0 mb-1'>Additional Information</h3>
          <div className="details-grid">
            <div><strong>Last Updated</strong></div><div>{formatDateTime(site.updatedAt ?? site.modifiedAt ?? site.createdAt)}</div>
            <div><strong>Virtual Tenants</strong></div><div>{virtualTenants ? virtualTenants.length : '—'}</div>
            <div><strong>CSH Alarms</strong></div><div>{bureauAlarms.length > 0 ? bureauAlarms.length : '—'}</div>
          </div>
        </section>

        

        {/* Acknowledge Modal */}
        <Modal
          isOpen={bulkMode === 'acknowledge' && selectedAlarms.length > 0}
          onCancel={() => {
            setBulkMode(null);
            setSelectedIds({});
          }}
          onConfirm={async () => {
            if (selectedAlarms && selectedAlarms.length > 0) {
              await handleBulkAcknowledge(selectedAlarms);
            }
          }}
          title={`Acknowledge ${selectedAlarms.length} Alarm${selectedAlarms.length !== 1 ? 's' : ''}`}
          content={
            bulkMode === 'acknowledge' && selectedAlarms.length > 0 && (
              <BureauAcknowledgeForm
                selectedAlarms={selectedAlarms}
                onAcknowledge={async (alarms) => {
                  await handleBulkAcknowledge(alarms);
                  setBulkMode(null);
                  setSelectedIds({});
                }}
                actionInFlight={actionInFlight}
                onRemoveSelected={(id: number) => setSelectedIds(prev => { const copy = { ...prev }; delete copy[id]; return copy; })}
                onClose={() => { setBulkMode(null); setSelectedIds({}); }}
                isInsideModal={true}
              />
            )
          }
        />

        {/* Status messages for actions (moved here so rule-related messages show above Rules) */}
        {successMessage && (
          <StatusMessage kind="success" onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </StatusMessage>
        )}
        {errorMessage && (
          <StatusMessage kind="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </StatusMessage>
        )}

        <section className="alarm-content mb-2">
          <div className="alarm-content--heading" >
              <h3 className="heading heading__title--info">Rules</h3>
              
              {!editingRuleId && site?.id && (
                <div>
                  <button
                    type="button"
                    className="btn btn-add-new"
                    onClick={() => {
                      setNewRules([{ ruleName: '', isActive: true, isUltraCritical: false, bindPathPriority: true, distributionGroupIds: '', attachmentGroupIds: '', filteringPaths: '', filteringPriorities: [], hideRtn: false, rtnExceptions: '' }]);
                      setIsCreateModalOpen(true);
                    }}
                  >
                    Add Rule
                  </button>
                </div>
              )}
          </div>
          {rulesLoading && <LoadingSpinner message="Loading rules…" />}
          {rulesError && (
            <StatusMessage kind="error" onClose={() => setRulesError(null)}>
              {`Error loading rules: ${rulesError}`}
            </StatusMessage>
          )}
          
          {/* Show rules table if there are any rules */}
          {siteRules && siteRules.length > 0 && (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Rule Name</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'right' }}>Distribution Groups</th>
                    <th style={{ textAlign: 'right' }}>Attachment Groups</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {siteRules.map(rule => (
                    <tr key={rule.siteRuleId}>
                      <td>{rule.ruleName}</td>
                      <td style={{ textAlign: 'center' }}><span className={rule.isActive ? 'badge badge--active' : 'badge badge--inactive'}>{rule.isActive ? 'Active' : 'Inactive'}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        {(() => {
                          const d = siteRuleDetails?.[rule.siteRuleId]?.distributionGroups;
                          if (Array.isArray(d) && d.length > 0) return d.map((g: { profileName?: string }) => g.profileName).join(', ');
                          return 'None';
                        })()
                        }
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {(() => {
                          const a = siteRuleDetails?.[rule.siteRuleId]?.attachmentGroups;
                          if (Array.isArray(a) && a.length > 0) return a.map((g: { profileName?: string }) => g.profileName).join(', ');
                          return 'None';
                        })()
                        }
                      </td>
                      <td>{formatDateTime(rule.createdAt)}</td>
                      <td>
                        <ActionList
                          customActions={[
                            ...(rule.isActive ? [{
                              label: 'Deactivate',
                              onClick: () => handleDeactivateRule(rule.siteRuleId),
                              className: 'actions__item--deactivate'
                            }] : [{
                              label: 'Activate',
                              onClick: () => handleActivateRule(rule.siteRuleId),
                              className: 'actions__item--acknowledge'
                            }]),
                            {
                              label: 'Edit',
                              onClick: () => handleEditRule(rule.siteRuleId),
                              className: 'actions__item--edit'
                            },
                            // If the rule is inactive, show Delete as well
                            ...(!rule.isActive ? [{
                              label: 'Delete',
                              onClick: () => handleDeleteRule(rule.siteRuleId),
                              className: 'actions__item--delete'
                            }] : [])
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Show "No rules" message only when not loading, no error, and truly no rules */}
          {!rulesLoading && !rulesError && (!siteRules || siteRules.length === 0) && (
            <div className='pb-1'><p>No rules configured for this site.</p></div>
          )}
          
        </section>

        <section className="alarm-content mb-2 p-1">
          <div className="alarm-content--heading">
            <h3 className='heading heading__title--virtual-tenant'>Virtual Tenants</h3>
            {id && site && (
              <div>
                <button
                  type="button"
                  className="btn btn-add-new"
                  onClick={() => setIsVTModalOpen(true)}
                >
                  Add Virtual Tenant
                </button>
              </div>
            )}
          </div>

          {id && site && virtualTenants && virtualTenants.length > 0 && (
            <div className="table-responsive">
              <VirtualTenantsList
                items={virtualTenants as unknown as VirtualTenantDetail[]}
                siteId={id}
                onEdit={async (vt) => {
                  // Fetch the VT's alarm paths so the edit form can display them
                  try {
                    const pathsRes = await getVirtualTenantAlarmPaths(vt.id);
                    let alarmPathIds: number[] = [];
                    if (pathsRes?.success && pathsRes.data) {
                      // API may return array of objects or array of numbers
                      alarmPathIds = Array.isArray(pathsRes.data) 
                        ? pathsRes.data.map((item: number | { alarmPathId?: number; id?: number }) => 
                            typeof item === 'number' ? item : (item.alarmPathId ?? item.id)
                          ).filter((pathId): pathId is number => typeof pathId === 'number')
                        : [];
                    }
                    // Enrich the VT object with numeric alarm path IDs for the form
                    setEditingVT({
                      ...vt,
                      alarmPathIds, // numeric array for the form
                    } as VirtualTenantLike);
                    setIsVTEditModalOpen(true);
                  } catch (e) {
                    console.info('[SiteDetails] Failed to fetch VT alarm paths:', e);
                    // Open modal anyway with whatever data we have
                    setEditingVT(vt as unknown as VirtualTenantLike);
                    setIsVTEditModalOpen(true);
                  }
                }}
                onDeleted={async () => {
                  await new Promise(resolve => setTimeout(resolve, 200));
                  try {
                    const res = await getSite(id);
                    if (res?.success && res.data) {
                      setSite(res.data as unknown as SiteDetail);
                      const vts = toVirtualTenants(res.data.children);
                      const enriched = await enrichVirtualTenants(vts, siteRulesRef.current || []);
                      setVirtualTenants(enriched);
                    }
                  } catch (err: unknown) {
                    // ignore
                    setErrorMessage(err instanceof Error ? err.message : String(err));
                  }
                }}
              />
            </div>
          )}

          {(!virtualTenants || virtualTenants.length === 0) && (
            <div className='pb-1'><p>No virtual tenants configured for this site.</p></div>
          )}
        </section>
      </div>

      {/* Edit Rule Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingRuleId(null);
          setEditingRuleData(null);
          setShowEditErrorSummary(false);
        }}
        onConfirm={() => {
          if (editingRuleId && editingRuleData) {
            return handleUpdateRule(editingRuleId, editingRuleData);
          }
        }}
        title="Edit Rule"
        content={
          editingRuleData && (
            <>
              <h3 className='heading__title heading__title--info mt-1 mb-1'>Rule Configuration</h3>
              <RulesForm
                value={[editingRuleData]}
                onChange={(data) => setEditingRuleData(data[0] || null)}
                disabled={false}
                siteId={id ? Number(id) : undefined}
                hideBuildingSelector={true}
                editIndex={0}
                onValidate={(isValid) => setIsEditFormValid(isValid)}
                showErrorSummary={showEditErrorSummary}
                onCloseErrorSummary={() => setShowEditErrorSummary(false)}
              />
            </>
          )
        }
      />

      {/* Create Rules Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onCancel={() => {
          setIsCreateModalOpen(false);
          setNewRules([]);
          setShowCreateErrorSummary(false);
        }}
        onConfirm={handleCreateRules}
        title="Create New Rules"
        content={
          <>
            <h3 className='heading__title heading__title--info mt-1 mb-1'>Rule Configuration</h3>
            <RulesForm
              value={newRules}
              onChange={setNewRules}
              disabled={false}
              siteId={id ? Number(id) : undefined}
              hideBuildingSelector={true}
              onValidate={(isValid) => setIsCreateFormValid(isValid)}
              showErrorSummary={showCreateErrorSummary}
              onCloseErrorSummary={() => setShowCreateErrorSummary(false)}
            />
          </>
        }
      />

      {/* Virtual Tenant Modal */}
      <Modal
        isOpen={isVTModalOpen}
        onCancel={() => setIsVTModalOpen(false)}
        onConfirm={async () => {
          const form = document.querySelector('[data-vt-form]') as HTMLFormElement;
          if (form) {
            form.requestSubmit();
          }
        }}
        title="Add Virtual Tenant"
        content={
          id && site && (
            <>
              <h3 className='heading__title heading__title--info mt-1 mb-1'>Virtual Tenant Configuration</h3>
              <VirtualTenantsForm
                siteId={id}
                parentSiteId={Number(site.id)}
                initialItems={(virtualTenants ?? []) as unknown as VirtualTenantDetail[]}
                isModal={true}
                onCreated={async () => {
                  setIsVTModalOpen(false);
                  await new Promise(resolve => setTimeout(resolve, 200));
                  try {
                    const res = await getSite(id);
                    if (res?.success && res.data) {
                      setSite(res.data as unknown as SiteDetail);
                      const vts = toVirtualTenants(res.data.children);
                      const enriched = await enrichVirtualTenants(vts, siteRulesRef.current || []);
                      setVirtualTenants(enriched);
                    }
                  } catch (err: unknown) {
                    setErrorMessage(err instanceof Error ? err.message : String(err));
                  }
                }}
                onDeleted={async () => {}}
              />
            </>
          )
        }
      />

      {/* Virtual Tenant Edit Modal */}
      <Modal
        isOpen={isVTEditModalOpen}
        onCancel={() => {
          setIsVTEditModalOpen(false);
          setEditingVT(null);
        }}
        onConfirm={async () => {
          const form = document.querySelector('[data-vt-edit-form]') as HTMLFormElement;
          if (form) {
            form.requestSubmit();
          } else {
            console.info('[SiteDetails] Edit form not found with selector [data-vt-edit-form]');
          }
        }}
        title="Edit Virtual Tenant"
        content={
          editingVT && (
            <>
              <h3 className='heading__title heading__title--info mt-1 mb-1'>Virtual Tenant Configuration</h3>
              <VirtualTenantsForm
                siteId={id!}
                parentSiteId={Number(site?.id)}
                initialItems={(virtualTenants ?? []) as unknown as VirtualTenantDetail[]}
                isModal={true}
                isEdit={true}
                editingItem={editingVT as unknown as VirtualTenantDetail}
                onUpdated={async () => {
                  setIsVTEditModalOpen(false);
                  setEditingVT(null);
                  if (id) {
                    await new Promise(resolve => setTimeout(resolve, 600));
                    try {
                      const res = await getSite(id);
                      if (res?.success && res.data) {
                        setSite(res.data as unknown as SiteDetail);
                        const vts = toVirtualTenants(res.data.children);
                        const enriched = await enrichVirtualTenants(vts, siteRulesRef.current || []);
                        setVirtualTenants(enriched);
                      }
                    } catch (e: unknown) {
                      console.info('[SiteDetails] Error in onUpdated:', e);
                    }
                  }
                }}
                onCreated={async () => {}}
                onDeleted={async () => {}}
              />
            </>
          )
        }
      />
      <Modal
        isOpen={confirmModalOpen}
        title={confirmModalTitle}
        headerClass={confirmModalHeaderClass}
        content={<ModalContent heading={confirmModalTitle} message={confirmModalMessage} />}
        onConfirm={() => {
          setConfirmModalOpen(false);
          confirmResolverRef.current?.(true);
        }}
        onCancel={() => {
          setConfirmModalOpen(false);
          confirmResolverRef.current?.(false);
        }}
      />
    </main>
  );
};

export default SiteDetails;
